
// Packages
import { defineComponent, PropType } from 'vue';

// Helpers
import {
  format,
  add,
  isBefore,
  DATE_TIME_FORMATS,
  utcToZonedTime,
  parseISO,
  guessTimezone
} from '@white-label-helper/date-utilities';
import isEqual from 'lodash/isEqual';
import { required } from '@white-label-helper/vuelidate';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { closeModal, popstateEventHandler, pushModalState } from '@white-label-helper/helper-history-modal';

// Constants
import {
  PARKINGS_TIMEPICKER_STEPS,
  NAMED_ROUTES,
  PRODUCT_TYPES,
  CANT_FIND_FLIGHT_ID,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';

import DateTimePicker from '../date-time-picker/date-time-picker.vue';
import {
  TextField,
  DropdownWrapper,
  ControlButton,
  Backdrop,
  FlightLookup,
} from 'ui-shared-components';
import CustomersSelector from '../customers-selector/customers-selector.vue';

// Types
import type VueI18n from 'vue-i18n';
import type {
  FormData,
  Terminal,
  Time,
  ParkingFormData
} from '@white-label-types/component';
import { Airport } from '@white-label-types/search-box';
import { HTMLElementWithDropdown } from '../../types/types';
// Store
import { mapState, mapActions } from 'pinia';
import { useManageBookingStore } from '@white-label-store/manage-booking';
import { useUserInfoStore } from '@white-label-store/user-info';
import type { CustomersOptions } from '@white-label-types/customers';
import { selectDate } from 'ecommerce/shared-helpers';
import { useSearchResultsStore } from '@white-label-store/search-results';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import AirportSelect from '../airport-select/airport-select.vue';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';

export default defineComponent({
  name: 'SearchParkingEditForm',

  components: {
    AirportSelect,
    Backdrop,
    ControlButton,
    DateTimePicker,
    DropdownWrapper,
    IconButton: () => import('@white-label-icon/icon-button'),
    TextField,
    CustomersSelector,
    FlightLookup,
  },

  props: {
    timeZone: {
      type: String,
      default: guessTimezone()
    },

    formData: {
      type: Object as PropType<ParkingFormData>,
      default: () => {
      }
    },

    updatingSearch: {
      type: Boolean,
      default: false
    },
    apiRequestRunning: {
      type: Boolean,
      default: false
    },
    customErrorMessages: {
      type: Object,
      required: false,
      default: () => {
      }
    },
  },

  data() {
    return {
      removePopstateHandlerCustomers: null,
      removePopstateHandlerTerminal: null,
      terminals: [] as Terminal[],
      airport: '' as ParkingFormData['airport'],
      terminal: '' as ParkingFormData['terminal'],
      entryDate: '' as ParkingFormData['entryDate'],
      entryTime: null as ParkingFormData['entryTime'],
      exitDate: '' as ParkingFormData['exitDate'],
      exitTime: null as ParkingFormData['exitTime'],
      outboundFlight: null,
      customers: {},
      dropdownStatuses: {
        entryDateTimePickerFocused: false,
        exitDateTimePickerFocused: false,
        discountFieldFocused: false,
        terminalOpened: false,
        customersOpened: false
      } as Record<string, boolean>,
      exitDateRequiredMessage: this.$t(
        'validationError.exitDateTime.dateError'
      ) as VueI18n.TranslateResult,
      exitTimeRequiredMessage: this.$t(
        'validationError.exitDateTime.timeError'
      ) as VueI18n.TranslateResult,
      exitDateTimeRequiredMessage: this.$t(
        'validationError.exitDateTime.dateTimeError'
      ) as VueI18n.TranslateResult,
      entryDateTimeRequiredMessage: this.$t(
        'validationError.entryDateTime.dateTimeError'
      ) as VueI18n.TranslateResult,
      entryDateRequiredMessage: this.$t(
        'validationError.entryDateTime.dateError'
      ) as VueI18n.TranslateResult,
      entryTimeRequiredMessage: this.$t(
        'validationError.entryDateTime.timeError'
      ) as VueI18n.TranslateResult,
      exitTimeBeforeEntryErrorMessage: this.$t(
        'validationError.exitTimeIsBeforeEntryTime'
      ) as VueI18n.TranslateResult,
      PARKINGS_TIMEPICKER_STEPS,
      maxDatePickerDate: add(new Date(), { years: 2 }) as Date,
      discount: null as FormData['discount'],
      entryTimeHaveChanged: false,
      customersError: '',
      bundlesPrePurchase: false,
      isFormSubmitted: false,
    };
  },

  computed: {
    ...mapState(useManageBookingStore, ['readAmendItem', 'readBooking']),
    ...mapState(useSearchResultsStore, [
      'utmSource',
      'utmMedium'
    ]),
    ...mapState(useUserInfoStore, {
      outboundFlightLookupItems: 'readOutboundFlightLookupItems',
      outboundFlightSelected: 'readOutboundFlightLookupSelected',
    }),

    outboundFlightVal() {
      return this.outboundFlight || this.outboundFlightSelected;
    },
    customersBackdrop: {
      get(): boolean {
        return this.isMobileResolution && this.dropdownStatuses.customersOpened;
      },
      set(): void {
        this.dropdownStatuses.customersOpened = false;
      }
    },
    amendItem(): ReturnType<typeof readAmendItem> {
      return this.readAmendItem;
    },

    originalBooking(): ReturnType<typeof readBooking> {
      return this.readBooking;
    },

    airports(): Airport[] {
      return getAppVariable('pois') || [];
    },

    isMultiAirportPartner(): boolean {
      return this.airports.length > 1;
    },

    multiAirportTerminals(): Terminal[] {
      const allSelectedTerminals: Terminal[] = getAppVariable('terminals');
      const airportTerminals = this.airports.find(
        (item) => item.code === this.airport
      )?.terminals;

      if (airportTerminals) {
        return airportTerminals.filter((t1) =>
          allSelectedTerminals.some((t2) => t1.id === t2.id)
        );
      } else {
        return [];
      }
    },

    terminalsWithDefault(): Terminal[] {
      return [
        ...(this.isMultiAirportPartner
          ? this.multiAirportTerminals
          : this.terminals),
        {
          name: this.$t('searchBox.dontKnowOption'),
          id: 'dontKnow'
        }
      ];
    },

    computedTerminal: {
      get(): Terminal {
        return (
          this.terminalsWithDefault.find(
            (terminal) => terminal.id.toString() === this.terminal?.toString()
          ) || {
            name: '',
            id: ''
          }
        );
      },
      set(val: Terminal): void {
        this.terminal = val.id.toString();
      }
    },

    timezoneOffset(): Date {
      return utcToZonedTime(new Date(), this.timeZone);
    },

    isTerminalsExists(): boolean {
      return this.terminals?.length > 1;
    },

    showTerminalSelect() {
      if (this.displayFlightLookup) return false;

      return this.isMultiAirportPartner
        ? !!this.multiAirportTerminals.length
        : this.isTerminalsExists;
    },

    incorrectTime(): boolean {
      if (this.entryDate === this.exitDate) {
        if (parseInt(this.exitTime?.value) <= parseInt(this.entryTime?.value)) {
          return true;
        }
      }
      return false;
    },

    formHasChanges(): boolean {
      const isEntryTimeChanged = !isEqual(
        this.entryTime,
        this.formData.entryTime
      );

      const isAirportChanged = this.airport !== this.formData.airport;
      const isExitTimeChanged = !isEqual(this.exitTime, this.formData.exitTime);
      const isEntryDateChanged = this.entryDate !== this.formData.entryDate;
      const isExitDateChanged = this.exitDate !== this.formData.exitDate;
      const isTerminalChangedIfTerminalsExists = this.isTerminalsExists
        ? this.terminal !== this.formData.terminal
        : false;
      const isDiscountChanged = (!!this.discount || !!this.formData.discount)
        && (this.discount !== this.formData.discount);
      const isCustomersChanged = this.showGuestsSelector ? !isEqual(this.customers, this.formData?.customers) : false;

      const entryTime = this.entryTime?.value ?? '';
      const exitTime = this.exitTime?.value ?? '';
      const entryDate = this.entryDate ?? '';
      const exitDate = this.exitDate ?? '';

      const isOutboundFlightChanged = this.displayFlightLookup
        ? this.outboundFlight?.id !== this.formData.outboundFlight?.id
        : false;

      const formHasChanged = (
        isAirportChanged ||
        isEntryTimeChanged ||
        isExitTimeChanged ||
        isEntryDateChanged ||
        isExitDateChanged ||
        isTerminalChangedIfTerminalsExists ||
        isDiscountChanged ||
        isCustomersChanged ||
        isOutboundFlightChanged);

      const currentFormData: Record<string, string | ParkingFormData['terminal']> = {
        entryTime,
        exitTime,
        entryDate,
        exitDate,
        terminal: this.terminal,
        discount: this.discount
      };

      const previousSearchParams = localStorage.getItem('previousSearchParams');

      /** as the page loads we store the  */
      let parsedParams = JSON.parse(previousSearchParams ?? '{}');
      // let isFormDataModified = false

      if (!this.showGuestsSelector) {
        delete parsedParams.customers;
      }

      /** ensure that we have matching objects to compare */
      delete parsedParams?.airport;
      parsedParams = {
        ...parsedParams,
        entryTime: parsedParams?.entryTime?.value,
        exitTime: parsedParams?.exitTime?.value
      };

      const criteriaSameAsPreviousSearch = isEqual(currentFormData, parsedParams);

      const isFormDataModified = criteriaSameAsPreviousSearch ? false : true;

      return (
        isFormDataModified && formHasChanged
      );
    },

    exitNotBeforeEntryError(): boolean {
      // @ts-ignore
      return !this.$v.time.exitNotBeforeEntry;
    },
    showExitCommonErrorMessage(): boolean {
      if (this.apiRequestRunning) return false;
      // @ts-ignore
      return !this.$v.time.exitNotBeforeEntry;
    },
    entryDateError(): boolean {
      if (this.apiRequestRunning) return false;
      if (!this.customErrorMessages?.date1?.length) return false;
      if (this.customErrorMessages.date1) {
        if (this.entryTimeHaveChanged) return false;
        return true;
      }
      // @ts-ignore
      return this.$v.entryDate.$error;
    },

    entryTimeError(): boolean {
      if (this.apiRequestRunning) return false;
      if (!this.customErrorMessages?.time1?.length) return false;
      if (this.customErrorMessages.time1.length > 0) {
        if (this.entryTimeHaveChanged) return false;
        return true;
      }
      // @ts-ignore
      return this.$v.entryTime.$error;
    },

    exitDateError(): boolean {
      if (this.apiRequestRunning) return false;
      if (!this.customErrorMessages?.date2?.length) return false;
      if (this.customErrorMessages.date2) {
        if (this.entryTimeHaveChanged) return false;
        return true;
      }
      // @ts-ignore
      return this.$v.exitDate.$error;
    },

    exitTimeError(): boolean {
      if (this.apiRequestRunning) return false;
      if (!this.customErrorMessages?.time2?.length) return false;
      if (this.customErrorMessages.time2) {
        if (this.entryTimeHaveChanged) return false;
        return true;
      }
      // @ts-ignore
      return this.$v.exitTime.$error;
    },

    terminalError(): boolean {
      // @ts-ignore
      return this.$v.terminal?.$error;
    },

    airportError(): boolean {
      // @ts-ignore
      return this.$v.airport?.$error;
    },
    entryErrorMessage(): VueI18n.TranslateResult | string {
      if (this.apiRequestRunning) return '';
      // @ts-ignore
      if (this.customErrorMessages?.hasOwnProperty('time1')) {
        // @ts-ignore
        return this.customErrorMessages.time1;
      }
      if (this.customErrorMessages?.hasOwnProperty('date1')) {
        // @ts-ignore
        return this.customErrorMessages.date1;
      }
      if (this.entryDateError && this.entryTimeError) {
        return this.entryDateTimeRequiredMessage;
      }
      if (this.entryDateError) {
        return this.entryDateRequiredMessage;
      }
      if (this.entryTimeError) {
        return this.entryTimeRequiredMessage;
      }
      return '';
    },

    exitErrorMessage(): VueI18n.TranslateResult | string {
      if (this.apiRequestRunning) return '';
      // @ts-ignore
      if (this.customErrorMessages?.hasOwnProperty('time2')) {
        // @ts-ignore
        return this.customErrorMessages.time2;
      }
      if (this.customErrorMessages?.hasOwnProperty('date2')) {
        // @ts-ignore
        return this.customErrorMessages.date2;
      }
      if (this.exitDateError && this.exitTimeError) {
        return this.exitDateTimeRequiredMessage;
      }
      if (this.exitDateError) {
        return this.exitDateRequiredMessage;
      }
      if (this.exitTimeError) {
        return this.exitTimeRequiredMessage;
      }
      // @ts-ignore
      if (!this.$v.time.exitNotBeforeEntry) {
        return this.exitTimeBeforeEntryErrorMessage;
      }
      return '';
    },

    minExitDate(): Date {
      if (!this.entryDate || !this.entryTime?.value) {
        return new Date();
      }
      return add(
        parseISO(`${this.entryDate}T${this.entryTime.value}`),
        { hours: 1 },
      );
    },

    editFormClasses(): string {
      if (this.displayFlightLookup) {
        if (this.showGuestsSelector && this.isDiscountFieldEnabled) {
          return 'edit-form__body edit-form__body__with-flight-lookup--compact';
        }
        return 'edit-form__body edit-form__body__with-flight-lookup';
      } else if (this.showTerminalSelect && this.isDiscountFieldEnabled && this.showGuestsSelector) {
        return 'edit-form__body edit-form__body__with-multi-terminals-and-discount-and-guests';
      } else if (this.showTerminalSelect && this.isDiscountFieldEnabled) {
        return 'edit-form__body edit-form__body__with-multi-terminals-and-discount';
      } else if (this.showTerminalSelect && this.showGuestsSelector) {
        return 'edit-form__body edit-form__body__with-multi-terminals-and-guests';
      } else if (this.isDiscountFieldEnabled && this.showGuestsSelector) {
        return 'edit-form__body edit-form__body__with-discount-and-guests';
      } else if (this.showTerminalSelect) {
        return 'edit-form__body edit-form__body__with-multi-terminals';
      } else if (this.isDiscountFieldEnabled) {
        return 'edit-form__body edit-form__body__with-discount';
      } else if (this.showGuestsSelector) {
        return 'edit-form__body edit-form__body__with-guests';
      }
      return 'edit-form__body edit-form__body__single-terminal-without-discount';
    },

    isDiscountFieldEnabled(): boolean {
      return (
        getAppVariable(
          'discount_visibility_rules.is_enabled_in_results_page'
        ) && this.$route.path.startsWith(NAMED_ROUTES.search)
      );
    },

    customerGroups(): CustomersOptions | null {
      const customerGroups = getAppVariable(`products.lounges.customer_groups`)
        || getAppVariable(`products.fasttrack.customer_groups`) as CustomersOptions;

      if (!customerGroups) {
        return null;
      }

      return customerGroups;
    },

    isCustomerGroupsExists(): boolean {
      return Boolean(
        this.customerGroups && Object.keys(this.customerGroups).length > 0
      );
    },

    showGuestsSelector(): boolean {
      return this.bundlesPrePurchase === true
        && this.isCustomerGroupsExists
        && !this.amendItem;
    },

    computedCustomers(): { name: string; id: string } {
      if (!this.customers) {
        return { id: '', name: '' };
      }

      const { adult, senior, infant, child } = this.customers;
      const label = [];

      const getAgeText = (key: 'senior' | 'adult' | 'child' | 'infant', count: number) => {
        return 'x ' + this.$tc(`shared.customers.${key}`, count);
      };

      if (senior) {
        label.push(`${senior} ${getAgeText('senior', senior)}`);
      }
      if (adult) {
        label.push(`${adult} ${getAgeText('adult', adult)}`);
      }
      if (child) {
        label.push(`${child} ${getAgeText('child', child)}`);
      }
      if (infant) {
        label.push(`${infant} ${getAgeText('infant', infant)}`);
      }

      return label.length
        ? { id: 'id', name: label.join(', ') }
        : {
          name: '',
          id: ''
        };
    },

    displayFlightLookup(): boolean {
      const isParkingProduct = getAppHeroProduct(PRODUCT_TYPES.PARKING) === PRODUCT_TYPES.PARKING;
      const displayFlightLookupForParking = isParkingProduct && getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP_PARKING) === true;
      return getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP) === true
        && getAppVariable('is_flight_lookup_enabled')
        && displayFlightLookupForParking;
    },

    flightLookupEligibility(): boolean {
      return !!(
        this.formData.entryDate &&
        this.formData.entryTime &&
        (!this.isMultiAirportPartner || (this.isMultiAirportPartner && this.formData.airport))
      );
    },

    disableCloseClickingOutsideGuestsDropdown(): boolean {
      return getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') && !this.isMobileResolution;
    },

    isChildAgeConfigurationAvailable(){
      return Boolean(this.customerGroups?.child_age_configuration);
    },
  },

  validations() {
    const validationObject = {
      time: {
        exitNotBeforeEntry: () =>
          this.entryDate &&
          this.exitDate &&
          isBefore(
            this.getDate(this.entryDate, this.entryTime, this.timeZone),
            this.getDate(this.exitDate, this.exitTime, this.timeZone)
          )
      },
      entryTime: { required },
      entryDate: { required },
      exitTime: { required },
      exitDate: { required },
      airport: {},
      terminal: {}
    };

    if (this.isMultiAirportPartner) {
      validationObject.airport = {
        required
      };
    }

    if (this.isTerminalsExists) {
      validationObject.terminal = {
        required
      };
    }
    if (this.showGuestsSelector) {
      validationObject.customers = {
        validation: (value: {
          child: number;
          infant: number;
          adult: number;
          senior: number;
          children_ages: number[];
        }) => {
          this.customersError = '';
          if (!this.isCustomerGroupsExists) return true;
          if (!Object.values(value).some((item) => item > 0)) {
            this.customersError = this.$t('validationError.selectNumberOfGuests');
            return false;
          }

          if (this.isChildAgeConfigurationAvailable && value.child > 0 && (value.children_ages?.length || 0) < value.child) {
            this.customersError = this.$t('guestDetails.ageIsRequired');
            return false;
          }

          if ((!value.senior || value.senior === 0) && (!value.adult || value.adult === 0)) {
            this.customersError = this.$t('validationError.oneAdultRequired');
            return false;
          }
          return true;
        }
      };
    }
    return validationObject;
  },

  watch: {
    formData: {
      deep: true,
      handler() {
        this.updateFields();
      }
    },
    'dropdownStatuses.terminalOpened': {
      handler(value) {
        if (!getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
          return;
        }
        if (value) {
          pushModalState('terminalOpened');
        } else {
          closeModal();
        }
      }
    },
    'dropdownStatuses.customersOpened': {
      handler(value) {
        if (!getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
          return;
        }
        if (value) {
          pushModalState('customersOpened');
        } else {
            if (
                getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') === true &&
                this.$refs.customersSelector &&
                !this.$refs.customersSelector.isCustomerChildSelectorsValid()
            ) {
            this.dropdownStatuses.customersOpened = true;
            this.$refs.customersSelector.apply();
          }
          closeModal('customersOpened');
        }
      }
    },
    dropdownStatuses: {
      deep: true,
      handler(val) {
        if (getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') && !this.isMobileResolution && this.isFormSubmitted) {
            this.isFormSubmitted = false;
            return;
        }

        if (Object.values(val).some((item) => item) || this.formHasChanges) {
          this.updateDesktopBackdrop(true);
          return;
        }
        this.updateDesktopBackdrop(false);
      }
    },
    airport: {
      handler(value) {
        if (!this.terminalsWithDefault.find((item) => item.id === this.computedTerminal.id)) {
          this.terminal = '';
        }
      }
    }
  },

  beforeDestroy() {
    if (getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
      if (this.removePopstateHandlerCustomers) {
        this.removePopstateHandlerCustomers();
      }
      if (this.removePopstateHandlerTerminal) {
        this.removePopstateHandlerTerminal();
      }
    }
  },

  created() {
    this.terminals = getAppVariable('terminals');
  },

  mounted() {
    this.updateFields();

    if (getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
      this.removePopstateHandlerCustomers = popstateEventHandler('customersOpened', this.onCustomersSelectorChange);
      this.removePopstateHandlerTerminal = popstateEventHandler('terminalOpened', () => this.dropdownStatuses.terminalOpened = false);
      this.dropdownStatuses.customersOpened = window.location.hash?.includes('customersOpened');
      this.dropdownStatuses.terminalOpened = window.location.hash?.includes('terminalOpened');
    }

    this.$nextTick(() => {
      selectDate(this.$refs.entryDate.$el, this.formData.entryDate);
      selectDate(this.$refs.exitDate.$el, this.formData.exitDate);
    });

    this.bundlesPrePurchase = getFeatureFlag('ECOM-2801_BUNDLES-PRE-PURCHASE', false);
    this.$launchDarkly.on('change:ECOM-2801_BUNDLES-PRE-PURCHASE', (value) => {
      this.bundlesPrePurchase = value;
    });
  },

  methods: {
    ...mapActions(useUserInfoStore, {
      fetchFlightLookupDetails: 'fetchFlightLookupDetails',
      storeOutboundFlightLookup: 'selectOutboundFlightLookup',
    }),
    selectOutboundFlightLookup(flight): void {
      if (flight && flight?.id !== CANT_FIND_FLIGHT_ID) {
        this.outboundFlight = flight;
      } else if (flight && flight?.id === CANT_FIND_FLIGHT_ID) {
        this.outboundFlight = { id: CANT_FIND_FLIGHT_ID };
      } else {
        this.outboundFlight = null;
        this.storeOutboundFlightLookup(this.outboundFlight);
      }
      this.terminal = flight?.terminal ?? 'dontKnow';
    },
    getFlightItems() {
      const entryDate = this.entryDate || this.formData.entryDate;
      const entryTime = this.entryTime || this.formData.entryTime;
      if (!entryDate || !entryTime) return;
      const dateTimeISO = `${entryDate}T${entryTime.value}:00.000Z`;
      const zonedDate = utcToZonedTime(new Date(dateTimeISO), 'UTC');
      const datetime = format(zonedDate, DATE_TIME_FORMATS.year_month_day_utc);
      const airportCode = this.formData.airport || getAppVariable('poi.code');
      this.fetchFlightLookupDetails({
        datetime,
        airportCode,
        direction: 'departure'
      });
    },
    setDropDownStatusesEntryDateTimePickerFocused(value: boolean) {
      this.dropdownStatuses.entryDateTimePickerFocused = value;
    },
    setDropdownStatusesDiscountFieldFocused(value: boolean) {
      this.dropdownStatuses.discountFieldFocused = value;
    },
    onCustomerSelectClose() {
      this.dropdownStatuses.customersOpened = false
    },

    onFocusEvent(type: string): void {
      if (type === 'entryDateTime' || type === 'exitDateTime') {
        this.closeDropdowns(type);
        this.updateDropdownStatuses(type);
      }

      if (type === 'selectTerminal') {
        this.closeDropdowns('entryDateTime');
        this.closeDropdowns('exitDateTime');
        this.updateDropdownStatuses(type);
      }

      if (type === 'discountField' || type === 'selectCustomers') {
        this.updateDropdownStatuses(type);
      }
      this.updateDesktopBackdrop(true);
    },

    closeDropdowns(type: string): void {
      const ref = type === 'entryDateTime' ? this.$refs.exitDate : this.$refs.entryDate;

      (ref as HTMLElementWithDropdown).timeDropdownOpened = false;
      (ref as HTMLElementWithDropdown).dateDropdownOpened = false;
    },

    updateDropdownStatuses(type: string): void {
      const statusKeys: Record<string, string> = {
        entryDateTime: 'entryDateTimePickerFocused',
        exitDateTime: 'exitDateTimePickerFocused',
        discountField: 'discountFieldFocused',
        selectTerminal: 'terminalOpened',
        selectCustomers: 'customersOpened'
      };

      Object.values(statusKeys).filter((key: string) => key !== type).forEach((key) => {
        this.dropdownStatuses[key] = false;
      });

      this.dropdownStatuses[type] = true;

    },
    updateFields() {
      this.airport = this.formData.airport;
      this.entryDate = this.formData.entryDate;
      this.entryTime = this.formData.entryTime
        ? { ...this.formData.entryTime }
        : null;
      this.exitDate = this.formData.exitDate;
      this.exitTime = this.formData.exitTime
        ? { ...this.formData.exitTime }
        : null;
      if (this.formData.terminal) {
        this.terminal = this.formData.terminal as string;
      } else if (this.isTerminalsExists) {
        this.terminal = 'dontKnow';
      }
      if (this.formData.discount) {
        this.discount = this.formData.discount as string;
      }
      if (this.formData.customers) {
        this.customers = this.formData.customers;
      }
      if (this.displayFlightLookup) {
        this.outboundFlight = this.formData.outboundFlight;
      }
    },
    getDate(date: string, timeObj: Time | null, timezone?: string): Date {
      const dateString = timeObj?.value
        ? `${date} ${timeObj?.value}`
        : `${date}`;
      if (timezone) {
        return utcToZonedTime(parseISO(dateString), timezone);
      }

      return parseISO(dateString);
    },

    onCancelChanges(): void {
      this.$emit('cancel-changes');
    },

    onSubmit(): void {
      if (this.displayFlightLookup) {
        this.selectOutboundFlightLookup(this.outboundFlight);
        this.$refs.flightLookup.closeDropdown();
      }
      if (getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') && !this.isMobileResolution) {
        if (this.$refs.customersSelector) {
          this.isFormSubmitted = true;
          this.$refs.customersSelector.apply();
        }
      }

      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$v.$reset();
        this.$emit('update-search', {
          airport: this.airport,
          entryDate: this.entryDate,
          entryTime: this.entryTime,
          exitDate: this.exitDate,
          exitTime: this.exitTime,
          terminal: this.terminal,
          discount: this.discount,
          customers: this.customers,
          ...(this.utmSource && { utmSource: this.utmSource }),
          ...(this.utmMedium && { utmMedium: this.utmMedium }),
          ...(this.displayFlightLookup && this.outboundFlight && { outboundFlight: this.outboundFlight })
        });
        this.updateDesktopBackdrop(false);
      }
      this.entryTimeHaveChanged = false;
    },

    onEntryDateChange(date: string): void {
      this.entryDate = date;
      this.entryTimeHaveChanged = true;

      const entryTimeIsNotAvailable =
        this.entryTime &&
        isBefore(
          parseISO(`${this.entryDate} ${this.entryTime.value}`),
          new Date()
        );

      if (entryTimeIsNotAvailable) {
        this.entryTime = null;
      }

      // Reset selected flight and get new flight items
      if (this.displayFlightLookup && this.entryDate !== this.formData.entryDate) {
        this.selectOutboundFlightLookup(null);
        this.getFlightItems();
        this.storeOutboundFlightLookup(this.outboundFlight);
      }
    },

    onExitDateChange(date: string): void {
      this.exitDate = date;
      this.entryTimeHaveChanged = true;

      const exitTimeIsNotAvailable =
        this.exitTime &&
        isBefore(
          parseISO(`${this.exitDate} ${this.exitTime.value}`),
          new Date()
        );

      if (exitTimeIsNotAvailable) {
        this.exitTime = null;
      }
    },
    generateFormData(
      item,
      bookingType: string
    ): Record<string, string> {

      const searchCriteria = bookingType === 'originalBooking'
        ? (item)?.searchCriteria
        : (item)?.search_criteria;

      return {
        entryTime: searchCriteria?.parking?.time1,
        exitTime: searchCriteria?.parking?.time2,
        entryDate: searchCriteria?.parking?.date1,
        exitDate: searchCriteria?.parking?.date2,
        terminal: searchCriteria?.parking?.terminal?.toString() ?? 'dontKnow',
        discount: searchCriteria?.parking?.discount ?? null
      };
    },

    onEntryTimeChange(time: Time | null): void {
      this.entryTime = time;
      // Reset selected flight and get new flight items
      if (this.displayFlightLookup && this.entryTime !== this.formData.entryTime) {
        this.selectOutboundFlightLookup(null);
        this.getFlightItems();
        this.storeOutboundFlightLookup(this.outboundFlight);
      }
    },

    onExitTimeChange(time: Time | null): void {
      this.exitTime = time;
    },

    updateDesktopBackdrop(value: boolean): void {
      this.$emit('update-desktop-bg', value);
    },

    onCustomersSelectorChange($event: { [key: string]: any }) {
      this.dropdownStatuses.customersOpened = false;
    },

    onCustomersUpdated($event: any) {
      if (!getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') || this.isMobileResolution) {
        return;
      }

      this.customers = $event;
    },

    outsideBackdropClicked() {
      if (!getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') || this.isMobileResolution) {
        this.dropdownStatuses.customersOpened = false;
        return;
      }

      if (this.dropdownStatuses.customersOpened) {
        this.updateDesktopBackdrop(true);
      }
      if (this.$refs.customersSelector) {
        this.$refs.customersSelector.apply();
      }
    },
    onChildNumberUpdated($event: { [key: string]: any }) {
      if (getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE')) {
        this.customers.child = $event;
        this.customers.children_ages = this.customers.children_ages?.slice(0, this.customers.child) ?? [];
      }
    }
  }
});
